/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        profileService.ts
 * @description Service API pour la gestion du profil utilisateur
 *              Fournit les methodes pour recuperer et modifier le profil
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import apiClient from './api';
import type { User } from '@/types';

/**
 * Donnees de mise a jour du profil
 */
export interface ProfileUpdateData {
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string | null;
}

/**
 * Donnees de changement de mot de passe
 */
export interface PasswordChangeData {
  oldPassword: string;
  newPassword: string;
}

/**
 * Reponse de mise a jour du profil
 */
export interface ProfileUpdateResponse {
  message: string;
  user: User;
}

/**
 * Reponse de changement de mot de passe
 */
export interface PasswordChangeResponse {
  message: string;
}

/**
 * Service de gestion du profil utilisateur
 */
export const profileService = {
  /**
   * Recupere le profil de l'utilisateur connecte
   * @returns Les informations du profil utilisateur
   */
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<User>('/profile');
    return response.data;
  },

  /**
   * Met a jour les informations du profil utilisateur
   * @param data Les nouvelles informations (nom, prenom, email, telephone)
   * @returns Le profil mis a jour avec un message de confirmation
   */
  updateProfile: async (data: ProfileUpdateData): Promise<ProfileUpdateResponse> => {
    const response = await apiClient.put<ProfileUpdateResponse>('/profile', data);
    return response.data;
  },

  /**
   * Change le mot de passe de l'utilisateur
   * @param oldPassword L'ancien mot de passe pour verification
   * @param newPassword Le nouveau mot de passe
   * @returns Message de confirmation
   */
  changePassword: async (
    oldPassword: string,
    newPassword: string
  ): Promise<PasswordChangeResponse> => {
    const response = await apiClient.patch<PasswordChangeResponse>('/profile/password', {
      oldPassword,
      newPassword,
    });
    return response.data;
  },
};

export default profileService;
